import { ViewBooking, ViewBookingAssignedAreasInner } from "collins-js-sdk";
import React, { useEffect, useMemo, useState } from "react";
import { IoChevronForward } from "react-icons/io5";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import {
  clearAllHighlightedAreas,
  getDefaultDuration,
  getDurationData,
  getRulesForDate,
  setSelectionFromGrid,
  toggleAreaFromGrid,
  toggleHighlightArea
} from "./AddBookingController";
import BookingTypeList from "./BookingTypeList";

/**
 * Parses a time string in HH:MM or HH:MM:SS format to hours and minutes
 */
const parseTimeString = (
  timeStr: string | null | undefined
): { hours: number; minutes: number } | null => {
  if (!timeStr) return null;
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(timeStr);
  if (!match) return null;
  return { hours: parseInt(match[1]), minutes: parseInt(match[2]) };
};

/**
 * Converts hours and minutes to a time string in HH:MM format
 */
const formatTimeString = (hours: number, minutes: number): string => {
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
};

/**
 * Clamps a time to the nearest allowed time within the min/max constraints,
 * respecting the minute step (default 5 minutes)
 */
const clampTimeToConstraints = (
  time: string | null | undefined,
  minTime: string | null | undefined,
  maxTime: string | null | undefined,
  minuteStep: number = 5
): string | null => {
  const parsed = parseTimeString(time);
  if (!parsed) return null;

  const minParsed = parseTimeString(minTime);
  const maxParsed = parseTimeString(maxTime);

  let { hours, minutes } = parsed;
  const totalMinutes = hours * 60 + minutes;

  // Check if time is below minimum
  if (minParsed) {
    const minTotalMinutes = minParsed.hours * 60 + minParsed.minutes;
    if (totalMinutes < minTotalMinutes) {
      // Snap to the first valid minute at or after minTime
      const snappedMinutes =
        Math.ceil(minParsed.minutes / minuteStep) * minuteStep;
      if (snappedMinutes >= 60) {
        // Overflow to next hour
        hours = minParsed.hours + 1;
        minutes = 0;
      } else {
        hours = minParsed.hours;
        minutes = snappedMinutes;
      }
      return formatTimeString(hours, minutes);
    }
  }

  // Check if time is above maximum
  if (maxParsed) {
    const maxTotalMinutes = maxParsed.hours * 60 + maxParsed.minutes;
    if (totalMinutes > maxTotalMinutes) {
      // Snap to the last valid minute at or before maxTime
      const snappedMinutes =
        Math.floor(maxParsed.minutes / minuteStep) * minuteStep;
      hours = maxParsed.hours;
      minutes = snappedMinutes;
      return formatTimeString(hours, minutes);
    }
  }

  // Time is within range, no adjustment needed
  return null;
};

import { TimePicker } from "@/components/blocks/time-picker";
import { useUserContext } from "../../../../../hooks/useUserContext";
import { WaitlistSlot } from "../../../../../types/areaAvailability";
import { BookableArea } from "../../../../../types/venues";
import {
  getBookingDuration,
  isBookingValid,
  isToTimeValid
} from "../../../../../utils/booking";
import {
  addDateTimeOffset,
  convertDateToUTC
} from "../../../../../utils/helpers";
import { getBookingEndTimeAsString } from "../../../../../utils/singleBooking";
import { EDSButton } from "../../../../eds/EDSButton";
import { EDSDatePicker } from "../../../../eds/EDSDatePicker";
import { EDSQuantityStepper } from "../../../../eds/EDSQuantityStepper";
import EDSTabs from "../../../../eds/EDSTabs";
import { Input } from "../../../../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../../ui/select";
import { Textarea } from "../../../../ui/textarea";
import FindAreasModal from "./FindAreas/FindAreasModal";
import SelectedAreaMessage from "./SelectedAreaMessage";

type AddBookingStep1Type = {
  booking: ViewBooking;
  updateBooking: (bookingProps: Partial<ViewBooking>) => void;
  handleWaitlistResponse?: (waitlistSlot?: WaitlistSlot) => void;
  setFullScreen: React.Dispatch<
    React.SetStateAction<React.JSX.Element | undefined>
  >;
  onWaitlistAdded?: () => void;
};

const AddBookingStep1 = ({
  booking,
  updateBooking,
  handleWaitlistResponse,
  setFullScreen,
  onWaitlistAdded
}: AddBookingStep1Type) => {
  const [viewAreas, setViewAreas] = useState<boolean>(false);
  const [waitlistSlotMins, setWaitlistSlotMins] = useState<number>(0);
  const [selectedAreas, setSelectedAreas] = useState<BookableArea[]>([]);
  const bookingContext = useDiaryContext();
  const userContext = useUserContext();
  const untilTime = getBookingEndTimeAsString(booking);
  const bookingNotValid = isBookingValid(booking);

  const timeConstraints = useMemo(() => {
    if (!booking.type?.id || !booking.date) {
      return { minTime: undefined, maxTime: undefined };
    }

    const bookingTypes = bookingContext.venueConfig?.booking_types;
    const typeDetails = bookingTypes?.find(
      (type) => type.id === booking.type?.id
    );

    if (!typeDetails) {
      return { minTime: undefined, maxTime: undefined };
    }

    const rules = getRulesForDate(typeDetails, new Date(booking.date));

    if (!rules) {
      return { minTime: undefined, maxTime: undefined };
    }

    return {
      minTime: rules.bookings_from,
      maxTime: rules.bookings_to
    };
  }, [
    booking.type?.id,
    booking.date,
    bookingContext.venueConfig?.booking_types
  ]);

  // Adjust booking time if it falls outside the allowed time constraints
  useEffect(() => {
    // Skip if booking is waitlisted (uses ASAP instead of specific time)
    if (booking.waitlisted) return;

    // Skip if no constraints are set
    if (!timeConstraints.minTime && !timeConstraints.maxTime) return;

    const clampedTime = clampTimeToConstraints(
      booking.time,
      timeConstraints.minTime,
      timeConstraints.maxTime,
      5 // minuteStep matches the TimePicker configuration
    );

    // Only update if the time needs to be adjusted
    if (clampedTime && clampedTime !== booking.time) {
      updateBooking({ time: clampedTime });
    }
  }, [
    timeConstraints.minTime,
    timeConstraints.maxTime,
    booking.time,
    booking.waitlisted
  ]);

  const updateBookingAndDuration = (props: Partial<ViewBooking>) => {
    const updatedBooking = { ...booking, ...props };
    const duration = getDefaultDuration(bookingContext, updatedBooking);

    updateBooking({
      ...updatedBooking,
      duration: duration ?? updatedBooking.duration
    });
  };

  useEffect(() => {
    clearAllHighlightedAreas();
    if (selectedAreas.length === 0) {
      toggleHighlightArea("unassigned");
    }
    const assignedAreas = selectedAreas.map((area) => {
      toggleHighlightArea(area.id);
      return {
        id: area.id,
        name: area.name,
        zone: area.zone
      } as unknown as ViewBookingAssignedAreasInner;
    });
    updateBooking({ assignedAreas });
    setTimeout(
      () =>
        bookingContext.setDiaryContext({
          highlightArea: selectedAreas.map((area) => area.id)
        }),
      150
    );
  }, [selectedAreas]);

  useEffect(() => {
    if (handleWaitlistResponse) {
      handleWaitlistResponse({
        minutes_until_available: waitlistSlotMins
      });
    }
  }, [waitlistSlotMins]);

  const gridAreaClick = bookingContext.gridAreaClick;
  useEffect(() => {
    if (!gridAreaClick) {
      return;
    }

    if (gridAreaClick.type === "double") {
      // set area, guests and time
      setSelectionFromGrid({
        gridAreaClick,
        bookingContext,
        booking,
        updateBookingAndDuration,
        setSelectedAreas
      });
    }

    if (gridAreaClick.type === "single") {
      // toggle areas
      toggleAreaFromGrid({
        gridAreaClick,
        bookingContext,
        selectedAreas,
        setSelectedAreas
      });
    }
  }, [gridAreaClick]);

  const showTypes = () => {
    setFullScreen(
      <BookingTypeList
        handleClose={() => setFullScreen(undefined)}
        updateBooking={(e) => {
          updateBookingAndDuration({
            ...e
          });
        }}
      />
    );
  };

  return (
    <div className="flex flex-col flex-1 h-full overflow-x-visible overflow-y-hidden">
      <div className="relative w-full flex items-center justify-center border-b border-border py-2.5 px-1 min-w-0 box-border">
        <EDSTabs
          tabs={[
            { id: "walkIn", label: "Walk in" },
            { id: "nonWalkIn", label: "Non walk in" }
          ]}
          selectedTab={booking.walkIn ? "walkIn" : "nonWalkIn"}
          onTabChange={(tabId) => {
            updateBooking({ walkIn: tabId === "walkIn" });
          }}
        />
      </div>
      <div className="overflow-x-visible overflow-y-auto h-0 flex-grow px-1 [[data-theme='Light']_&]:text-foreground">
        <div className="relative w-full flex items-center justify-between border-b border-border py-2.5 px-1 min-w-0 box-border">
          <p className="text-left font-bold text-[var(--formLabelFontSize)] text-foreground m-0 shrink-0 w-[132px] min-w-[132px] max-w-[132px]">
            Date
          </p>
          <EDSDatePicker
            date={booking.date ? new Date(booking.date) : new Date()}
            onSelect={(date) => {
              if (date) {
                updateBookingAndDuration({
                  date: addDateTimeOffset(date)
                });
              } else {
                updateBookingAndDuration({
                  date: new Date()
                });
              }
            }}
            placeholder="Select date"
          />
        </div>
        <div className="relative w-full flex items-center justify-between border-b border-border py-2.5 px-1 min-w-0 box-border">
          <p className="text-left font-bold text-[var(--formLabelFontSize)] text-foreground m-0 shrink-0 w-[132px] min-w-[132px] max-w-[132px]">
            Guests
          </p>
          <EDSQuantityStepper
            value={Number(booking?.numPeople) || 1}
            onChange={(numPeople) => {
              updateBookingAndDuration({
                numPeople,
                actualGuests: numPeople
              });
            }}
            min={1}
            max={100}
            step={1}
          />
        </div>
        <div className="relative w-full flex items-center justify-between border-b border-border py-2.5 px-1 min-w-0 box-border">
          <p className="text-left font-bold text-[var(--formLabelFontSize)] text-foreground m-0 shrink-0 w-[132px] min-w-[132px] max-w-[132px]">
            Type
          </p>
          <button
            type="button"
            className={[
              "py-2 flex items-center text-[var(--primaryFontSize)] w-full cursor-pointer flex-1 min-w-0 max-w-full focus:outline-2 focus:outline-ring focus:outline-offset-2",
              booking.type?.name
                ? "justify-between"
                : "justify-end gap-2 text-muted-foreground [[data-theme='Light']_&]:items-end [[data-theme='Light']_&]:gap-2.5 [[data-theme='Light']_&]:font-normal"
            ].join(" ")}
            onClick={() => {
              showTypes();
            }}
          >
            <span className="text-sm flex-1 min-w-0 mr-2 overflow-hidden text-ellipsis whitespace-nowrap">
              {booking.type?.name ?? "Select a Type"}
            </span>
            <IoChevronForward size={16} className="shrink-0" />
          </button>
        </div>

        <div className="flex justify-between items-center">
          <div className="relative w-full flex items-center justify-between border-b border-border py-2.5 px-1 min-w-0 box-border">
            <p className="text-left font-bold text-[var(--formLabelFontSize)] text-foreground m-0 shrink-0 w-[132px] min-w-[132px] max-w-[132px]">
              From
            </p>
            {booking.waitlisted === true ? (
              <Input
                disabled={true}
                value={"ASAP"}
                type="text"
                className="text-left font-normal text-sm text-foreground bg-transparent border-none p-0 focus:outline-2 focus:outline-ring focus:outline-offset-2 disabled:text-foreground disabled:opacity-50 w-6/12"
              />
            ) : (
              <TimePicker
                value={booking.time ?? ""}
                onChange={(value) =>
                  updateBookingAndDuration({
                    time: value
                  })
                }
                minuteStep={5}
                className="min-w-[140px] w-6/12 text-sm font-normal"
                format="24h"
                placeholder="Start Time"
                minTime={timeConstraints.minTime}
                maxTime={timeConstraints.maxTime}
              />
            )}
          </div>
        </div>
        <div className="flex justify-between items-center">
          <div className="relative w-full flex items-center justify-between border-b border-border py-2.5 px-1 min-w-0 box-border">
            <p className="text-left font-bold text-[var(--formLabelFontSize)] text-foreground m-0 shrink-0 w-[132px] min-w-[132px] max-w-[132px]">
              To
            </p>
            {booking.waitlisted === true ? (
              <Select
                value={booking.duration?.toString()}
                disabled={isToTimeValid(booking)}
                onValueChange={(value) => {
                  if (booking.duration?.toString() !== value) {
                    updateBooking({
                      duration: Number(value)
                    });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select duration" />
                </SelectTrigger>
                <SelectContent>
                  {getDurationData().map((item) => (
                    <SelectItem key={item.value} value={item.value.toString()}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <TimePicker
                value={untilTime}
                onChange={(value) =>
                  updateBooking({
                    duration: getBookingDuration(
                      booking.time ?? undefined,
                      value ?? untilTime
                    )
                  })
                }
                minuteStep={5}
                format="24h"
                placeholder="End Time"
                className="min-w-[140px] w-6/12"
                disabled={isToTimeValid(booking)}
                minTime={booking.time ?? undefined}
              />
            )}
          </div>
        </div>
        <div className="mb-[var(--wrapperBottomMargin)] mt-[var(--wrapperBottomMargin)] [[data-theme='Light']_&_button]:bg-primary [[data-theme='Light']_&_button]:h-10 [[data-theme='Light']_&_button]:text-primary-foreground [[data-theme='Light']_&_button]:text-[13px] [[data-theme='Light']_&_button:hover]:bg-primary/90">
          <EDSButton
            variant="default"
            className="w-full"
            disabled={bookingNotValid}
            onClick={() => setViewAreas(true)}
          >
            Find Areas
          </EDSButton>
        </div>
        <SelectedAreaMessage
          waitlistSlotMins={waitlistSlotMins}
          selectedAreas={selectedAreas}
          booking={booking}
        />
        <div className="mt-5">
          <p className="text-left font-bold text-[var(--formLabelFontSize)] text-foreground m-0 shrink-0 w-[132px] min-w-[132px] max-w-[132px]">
            Run Sheet Notes
          </p>
          <Textarea
            rows={3}
            id="textAreaId"
            name="runSheetNotes"
            placeholder="Add notes"
            className="w-[17rem] ml-1 mr-0 mt-3 mb-0 rounded-[calc(var(--radius)_-_2px)] border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm leading-5 text-[hsl(var(--foreground))] shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] transition-[colors] duration-200 resize-y box-border block px-3 py-2 border-solid max-h-[200px]"
            onChange={(e) => {
              updateBooking({
                comments: [
                  {
                    comment: e.target.value,
                    fullName: `${userContext.user?.first_name} ${userContext.user?.last_name}`,
                    time: convertDateToUTC(new Date()).toISOString()
                  }
                ]
              });
            }}
          />
        </div>
      </div>
      <FindAreasModal
        open={viewAreas}
        booking={booking}
        selectedAreas={selectedAreas}
        setSelectedAreas={(area) => {
          setSelectedAreas(area);
        }}
        handleClose={() => setViewAreas(false)}
        updateBooking={updateBooking}
        handleWaitlistResponse={(e) => {
          setWaitlistSlotMins(e?.minutes_until_available ?? 0);
        }}
        onWaitlistAdded={onWaitlistAdded}
      />
    </div>
  );
};

export default React.memo<AddBookingStep1Type>(AddBookingStep1);
